import { useEffect, useId, useMemo, useRef, useState } from "react";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Box, Divider, List, ListItemButton, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { de } from "date-fns/locale/de";

import { DayOfWeek, DutyColumnKind, DutyScheduleCounselorOptionResponse } from "@/api";
import {
  formatCounselorName,
  matchesCell,
  toDutyPreferenceLines,
} from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/data-entry/DutyScheduleDataEntry.utils";
import { useTranslation } from "@/translations";

const WEEKDAY_PATTERN = "EEEEEE";
const PREFERENCE_SEPARATOR = " · ";
const POPOVER_WIDTH = 560;
const LIST_MAX_HEIGHT = 360;
// The code and the hours keep these widths in every row, so the three texts read as columns.
const CODE_COLUMN_WIDTH = 48;
const HOURS_COLUMN_WIDTH = 54;

// No divider between rows: the rows are set apart by their own height, and a selected one by its tint.
const ROW_SX = { px: 3, py: 1.5 } as const;

interface Props {
  anchorEl: HTMLElement;
  // The duty being staffed: its column label, kind and day.
  dutyLabel: string;
  columnKind: DutyColumnKind;
  // Set for a court column, so the check mark can honour the preferred courts.
  columnCourtId: string | null;
  date: Date;
  // The counselor currently in the cell, so its row can be marked as the one in place.
  assignedCounselorId: string | null;
  counselors: DutyScheduleCounselorOptionResponse[];
  onSelect: (counselorId: string | null) => void;
  onClose: () => void;
}

// Every active staff member is offered, not only those whose availability matches: the check mark says
// who asked for this duty, and picking somebody who did not is allowed and warns on the cell.
export function DutyScheduleCounselorPicker({
  anchorEl,
  dutyLabel,
  columnKind,
  columnCourtId,
  date,
  assignedCounselorId,
  counselors,
  onSelect,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const titleId = useId();

  useEffect(() => searchRef.current?.focus(), []);

  const kindLabels: Record<DutyColumnKind, string> = {
    [DutyColumnKind.Aw1]: t("dutySchedule.detail.dataEntry.picker.duty.aw12"),
    [DutyColumnKind.Aw2]: t("dutySchedule.detail.dataEntry.picker.duty.aw12"),
    [DutyColumnKind.Aw3]: t("dutySchedule.detail.dataEntry.picker.duty.aw3"),
    [DutyColumnKind.Court]: t("dutySchedule.detail.dataEntry.picker.duty.court"),
  };

  const matching = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    if (searchTerm === "") {
      return counselors;
    }

    return counselors.filter((counselor) =>
      [counselor.userCode, counselor.firstName, counselor.lastName].join(" ").toLowerCase().includes(searchTerm)
    );
  }, [counselors, search]);

  const formatPreferences = (counselor: DutyScheduleCounselorOptionResponse) =>
    toDutyPreferenceLines(counselor)
      .map((line) => `${WEEKDAY_SHORT_LABELS[line.weekday]}: ${line.kinds.map((kind) => kindLabels[kind]).join(", ")}`)
      .join(PREFERENCE_SEPARATOR);

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          // The cell announces aria-haspopup="dialog", so what opens has to carry the role and a name
          // to match — otherwise a screen reader is promised a dialog and lands on an unnamed group.
          // Named by the heading already on screen rather than a duplicate aria-label.
          role: "dialog",
          "aria-labelledby": titleId,
          sx: { width: POPOVER_WIDTH },
        },
      }}
    >
      <Stack p={2} gap={1}>
        <Typography id={titleId} variant="body2" color="text.secondary">
          {t("dutySchedule.detail.dataEntry.picker.title", {
            duty: dutyLabel,
            weekday: format(date, WEEKDAY_PATTERN, { locale: de }),
          })}
        </Typography>

        {/* Focused through a ref rather than autoFocus: typing straight into the search is the point
            of opening the overlay, and this way the focus move happens only on a real open. The
            placeholder carries no accessible name, so the label is given to the input directly. */}
        <TextField
          size="small"
          inputRef={searchRef}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("dutySchedule.detail.dataEntry.picker.search")}
          slotProps={{ htmlInput: { "aria-label": t("dutySchedule.detail.dataEntry.picker.search") } }}
        />
      </Stack>

      <Divider />

      <List sx={{ maxHeight: LIST_MAX_HEIGHT, overflow: "auto", py: 0 }}>
        {/* First entry on purpose: clearing a Dienst is a normal outcome, not an edge case. */}
        <ListItemButton sx={ROW_SX} selected={assignedCounselorId == null} onClick={() => onSelect(null)}>
          <Typography variant="body2" fontStyle="italic" color="text.secondary">
            {t("dutySchedule.detail.dataEntry.picker.unoccupied")}
          </Typography>
        </ListItemButton>

        {matching.map((counselor) => {
          const isAssigned = counselor.id === assignedCounselorId;
          const isPreferred = matchesCell(counselor, date, columnKind, columnCourtId);
          const preferences = formatPreferences(counselor);

          return (
            <ListItemButton
              key={counselor.id}
              sx={ROW_SX}
              selected={isAssigned}
              onClick={() => onSelect(counselor.id)}
              aria-label={[
                counselor.userCode,
                formatCounselorName(counselor),
                isPreferred ? t("dutySchedule.detail.dataEntry.picker.preferred") : undefined,
                isAssigned ? t("dutySchedule.detail.dataEntry.picker.assigned") : undefined,
              ]
                .filter(Boolean)
                .join(", ")}
            >
              {/* Fixed columns for the code and the hours, so they read as columns down the list and
                  only the preferences take the leftover width. */}
              <Stack direction="row" alignItems="center" gap={1} flex={1} minWidth={0}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="fsw.darkblue"
                  width={CODE_COLUMN_WIDTH}
                  flexShrink={0}
                >
                  {counselor.userCode}
                </Typography>
                <Typography variant="body2" fontWeight={600} width={HOURS_COLUMN_WIDTH} flexShrink={0}>
                  {t("dutySchedule.detail.dataEntry.picker.hours", { hours: counselor.weeklyHours })}
                </Typography>
                <PreferenceText preferences={preferences} />
              </Stack>
              {/* Held open when nobody asked for this duty, so the preference column keeps one width. */}
              <Box display="flex" width={20} ml={1} flexShrink={0} color="primary.main" aria-hidden>
                {isPreferred && <CheckCircleRoundedIcon fontSize="small" />}
              </Box>
            </ListItemButton>
          );
        })}

        {matching.length === 0 && (
          <Box px={2} py={1.5}>
            <Typography variant="body2" color="text.secondary">
              {t("dutySchedule.detail.dataEntry.picker.noResults")}
            </Typography>
          </Box>
        )}
      </List>
    </Popover>
  );
}

// The preferences are the first thing to lose room, so the full line is offered as a tooltip once the
// column can no longer show it. describeChild: the row already has an accessible name of its own.
function PreferenceText({ preferences }: { preferences: string }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    setIsTruncated(element != null && element.scrollWidth > element.clientWidth);
  }, [preferences]);

  const text = (
    <Typography ref={textRef} component="span" variant="caption" color="text.secondary" noWrap minWidth={0}>
      {preferences}
    </Typography>
  );

  if (!isTruncated) {
    return text;
  }

  return (
    <Tooltip title={preferences} describeChild>
      {text}
    </Tooltip>
  );
}

// Short weekday labels from date-fns rather than a hand-kept list. A preference line has no date
// behind it, so the labels come off a reference week; 2027-01-03 is a Sunday, matching the enum order.
const WEEKDAY_SHORT_LABELS: Record<DayOfWeek, string> = Object.fromEntries(
  [
    DayOfWeek.Sunday,
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
    DayOfWeek.Saturday,
  ].map((weekday, index) => [weekday, format(new Date(2027, 0, 3 + index), WEEKDAY_PATTERN, { locale: de })])
) as Record<DayOfWeek, string>;
